import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import { useState, useEffect, useMemo, useCallback } from 'react';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FiltersPanel from './FiltersPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { loadTimezonesGeoJSON } from '@/lib/timezoneLoader';
import { loadRegionsGeoJSON } from '@/lib/regionsLoader';

// Fix Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Create custom icons
const blueIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom cluster icon creation function
const createClusterCustomIcon = function (cluster) {
  const count = cluster.getChildCount();
  let size = 'small';
  let className = 'cluster-small';
  
  if (count > 100) {
    size = 'large';
    className = 'cluster-large';
  } else if (count > 50) {
    size = 'medium';
    className = 'cluster-medium';
  }

  return L.divIcon({
    html: `<div class="cluster-inner">${count}</div>`,
    className: `custom-cluster-icon ${className}`,
    iconSize: L.point(50, 50, true),
  });
};

export default function PortChecker({ portsData, countriesData, continentsData, regionsData, portRegionsData, timeZonesData, loading, onRefresh }) {
  const [filters, setFilters] = useState({
    euETS: false,
    fuelEU: false,
    countries: [],
    continents: [],
    regions: [],
    timeZones: [],
    noRegion: false,
    euCountriesOnly: false,
    searchText: '',
    showTimeZones: false,
    showRegions: false,
  });

  const [filteredPorts, setFilteredPorts] = useState([]);
  const [timeZonesGeoJSON, setTimeZonesGeoJSON] = useState(null);
  const [regionsGeoJSON, setRegionsGeoJSON] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Debug log to check data
  useEffect(() => {
    console.log('PortChecker received data:', {
      portsCount: portsData?.length,
      countriesCount: countriesData?.length,
      regionsCount: regionsData?.length,
      loading,
      samplePort: portsData?.[0],
      sampleCoords: portsData?.[0] ? {
        lat: portsData[0].latitude,
        lng: portsData[0].longitude,
        latType: typeof portsData[0].latitude,
        lngType: typeof portsData[0].longitude,
        latParsed: parseFloat(portsData[0].latitude),
        lngParsed: parseFloat(portsData[0].longitude)
      } : null
    });
  }, [portsData, countriesData, regionsData, loading]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const continents = useMemo(() => {
    if (!continentsData.length) return [];
    return continentsData
      .filter(c => c && c.name && typeof c.name === 'string')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [continentsData]);

  const regions = useMemo(() => {
    if (!regionsData.length) return [];
    return regionsData.filter(r => r.id && r.name).sort((a, b) => a.name.localeCompare(b.name));
  }, [regionsData]);

  const filteredPortsMemo = useMemo(() => {
    if (!portsData.length || !countriesData.length) return [];

    // First filter to only include ports with valid coordinates
    let filtered = portsData.filter(p => {
      const lat = parseFloat(p.latitude);
      const lng = parseFloat(p.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });

    if (filters.euETS) {
      filtered = filtered.filter(p => {
        const val = p.is_ets_enabled;
        return val === true || val === 'true' || val === 1 || val === '1';
      });
    }

    if (filters.fuelEU) {
      filtered = filtered.filter(p => {
        const val = p.is_fuel_eu_enabled;
        return val === true || val === 'true' || val === 1 || val === '1';
      });
    }

    if (filters.euCountriesOnly) {
      const euCountryIds = countriesData.filter(c => {
        const val = c.is_eu_country;
        return val === true || val === 'true' || val === 1 || val === '1';
      }).map(c => c.id);
      filtered = filtered.filter(p => euCountryIds.includes(p.country_id));
    }

    if (filters.countries.length > 0) {
      filtered = filtered.filter(p => filters.countries.includes(p.country_id));
    }

    if (filters.continents.length > 0) {
      const continentCountryIds = countriesData
        .filter(c => filters.continents.includes(c.continent_id))
        .map(c => c.id);
      filtered = filtered.filter(p => continentCountryIds.includes(p.country_id));
    }

    if (filters.regions.length > 0) {
      const regionPortIds = portRegionsData
        .filter(pr => filters.regions.includes(pr.region_id))
        .map(pr => pr.port_id);
      filtered = filtered.filter(p => regionPortIds.includes(p.id));
    }

    if (filters.noRegion) {
      const portsWithRegion = portRegionsData.map(pr => pr.port_id);
      filtered = filtered.filter(p => !portsWithRegion.includes(p.id));
    }

    if (filters.timeZones.length > 0) {
      filtered = filtered.filter(p => filters.timeZones.includes(String(p.time_zone_id)));
    }

    if (filters.searchText) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(filters.searchText.toLowerCase()));
    }

    console.log(`PortChecker: ${portsData.length} total ports, ${filtered.length} after filtering`);
    console.log('Sample filtered port:', filtered[0]);
    console.log('First 3 filtered ports coords:', filtered.slice(0, 3).map(p => ({
      id: p.id,
      name: p.name,
      lat: p.latitude,
      lng: p.longitude,
      latParsed: parseFloat(p.latitude),
      lngParsed: parseFloat(p.longitude),
      isValidLat: !isNaN(parseFloat(p.latitude)),
      isValidLng: !isNaN(parseFloat(p.longitude))
    })));

    return filtered;
  }, [filters, portsData, countriesData, portRegionsData, timeZonesData]);

  useEffect(() => {
    setFilteredPorts(filteredPortsMemo);
  }, [filteredPortsMemo]);

  useEffect(() => {
    if (filters.showTimeZones) {
      // Load local simplified timezone geojson files
      loadTimezonesGeoJSON()
        .then(data => setTimeZonesGeoJSON(data))
        .catch(err => {
          console.error('Error loading time zones:', err);
          // Fallback: create simple UTC offset grid lines
          const gridData = {
            type: "FeatureCollection",
            features: []
          };
          // Create vertical lines for UTC offsets from -12 to +14
          for (let offset = -12; offset <= 14; offset++) {
            const longitude = offset * 15; // 15 degrees per hour
            if (longitude >= -180 && longitude <= 180) {
              gridData.features.push({
                type: "Feature",
                properties: { 
                  tzid: `UTC${offset >= 0 ? '+' : ''}${offset}`,
                  offset: offset
                },
                geometry: {
                  type: "LineString",
                  coordinates: [[longitude, -85], [longitude, 85]]
                }
              });
            }
          }
          setTimeZonesGeoJSON(gridData);
        });
    }
  }, [filters.showTimeZones]);

  useEffect(() => {
    if (filters.showRegions) {
      loadRegionsGeoJSON()
        .then(data => setRegionsGeoJSON(data))
        .catch(err => {
          console.error('Error loading regions:', err);
          setRegionsGeoJSON(null);
        });
    }
  }, [filters.showRegions]);

  // Helper function to get current time and friendly name for a time zone
  const getTimeZoneInfo = useCallback((timezoneId) => {
    if (!timezoneId) return null;
    
    // Find the time zone data by ID (time_zone_id is numeric in port CSV)
    const tzData = timeZonesData?.find(tz => tz.id === String(timezoneId));
    if (!tzData || !tzData.value) return null;
    
    const friendlyName = tzData.name || tzData.value;
    
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tzData.value,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short'
      });
      return {
        time: formatter.format(now),
        name: friendlyName
      };
    } catch (error) {
      console.error('Error formatting time:', error);
      return null;
    }
  }, [timeZonesData]);

  const timeZoneStyle = {
    color: '#ff7800',
    weight: 2,
    opacity: 0.65,
    fillOpacity: 0.1
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-lg">Loading map data...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      <FiltersPanel
        filters={filters}
        setFilters={setFilters}
        countriesData={countriesData}
        continents={continents}
        regions={regions}
        timeZonesData={timeZonesData}
        portsData={portsData}
        totalPorts={portsData.length}
        visiblePorts={filteredPorts.length}
      />
      <div className="flex-1 relative h-full">
        {/* Refresh Button */}
        <div className="absolute top-4 right-4 z-[1000]">
          <Button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            size="sm"
            variant="secondary"
            className="shadow-lg"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Ports'}
          </Button>
        </div>
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />
          {filters.showTimeZones && timeZonesGeoJSON && (
            <GeoJSON 
              data={timeZonesGeoJSON} 
              style={timeZoneStyle}
              onEachFeature={(feature, layer) => {
                if (feature.properties) {
                  const tzName = feature.properties.tzid || feature.properties.name || feature.properties.TZID || 'Unknown';
                  
                  // Get current time for this time zone
                  let timeInfo = '';
                  try {
                    const now = new Date();
                    const formatter = new Intl.DateTimeFormat('en-US', {
                      timeZone: tzName,
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                      timeZoneName: 'short'
                    });
                    timeInfo = `<br/><strong>Current Time:</strong> ${formatter.format(now)}`;
                  } catch (e) {
                    // If timezone is not recognized, skip time display
                  }
                  
                  layer.bindPopup(`<strong>Time Zone:</strong> ${tzName}${timeInfo}`);
                }
              }}
            />
          )}
          {filters.showRegions && regionsGeoJSON && (
            <GeoJSON 
              data={regionsGeoJSON} 
              style={{
                color: '#FFA500',
                weight: 2,
                opacity: 0.7,
                fillColor: '#FFA500',
                fillOpacity: 0.1
              }}
              onEachFeature={(feature, layer) => {
                if (feature.properties && feature.properties.name) {
                  layer.bindPopup(`<strong>Region:</strong> ${feature.properties.name}`);
                }
              }}
            />
          )}
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={50}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            iconCreateFunction={createClusterCustomIcon}
          >
            {(() => {
              console.log(`Rendering ${filteredPorts.length} markers on map`);
              if (filteredPorts.length > 0) {
                console.log('First marker position:', [parseFloat(filteredPorts[0].latitude), parseFloat(filteredPorts[0].longitude)]);
                console.log('First port ETS value:', filteredPorts[0].is_ets_enabled, 'Type:', typeof filteredPorts[0].is_ets_enabled);
              }
              return filteredPorts.map(port => {
                const val = port.is_ets_enabled;
                const isEtsEnabled = val === true || val === 'true' || val === 1 || val === '1';
                return (
                  <Marker 
                    key={port.id} 
                    position={[parseFloat(port.latitude), parseFloat(port.longitude)]}
                    icon={isEtsEnabled ? greenIcon : blueIcon}
                  >
                <Popup>
                  <div className="p-1 min-w-[250px]">
                    <h3 className="font-bold text-base mb-3">{port.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">UN/LOCODE:</span>
                        <span className="font-medium">{port.un_locode}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Country:</span>
                        <span className="font-medium">{countriesData.find(c => c.id === port.country_id)?.name || 'Unknown'}</span>
                      </div>
                      {portRegionsData.length > 0 && (() => {
                        const portRegion = portRegionsData.find(pr => pr.port_id === port.id);
                        const region = portRegion ? regionsData.find(r => r.id === portRegion.region_id) : null;
                        if (region) {
                          return (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Region:</span>
                              <span className="font-medium">{region.name}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {port.time_zone_id && (() => {
                        const tzInfo = getTimeZoneInfo(port.time_zone_id);
                        if (tzInfo) {
                          return (
                            <>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Time Zone:</span>
                                <span className="font-medium text-xs">{tzInfo.name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Local Time:</span>
                                <span className="font-medium">{tzInfo.time}</span>
                              </div>
                            </>
                          );
                        }
                        return null;
                      })()}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">EU ETS:</span>
                        <Badge variant={(port.is_ets_enabled === true || port.is_ets_enabled === 'true' || port.is_ets_enabled === 1 || port.is_ets_enabled === '1') ? 'default' : 'secondary'}>
                          {(port.is_ets_enabled === true || port.is_ets_enabled === 'true' || port.is_ets_enabled === 1 || port.is_ets_enabled === '1') ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Fuel EU:</span>
                        <Badge variant={(port.is_fuel_eu_enabled === true || port.is_fuel_eu_enabled === 'true' || port.is_fuel_eu_enabled === 1 || port.is_fuel_eu_enabled === '1') ? 'default' : 'secondary'}>
                          {(port.is_fuel_eu_enabled === true || port.is_fuel_eu_enabled === 'true' || port.is_fuel_eu_enabled === 1 || port.is_fuel_eu_enabled === '1') ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
              });
            })()}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
